import { Flex, Tabs, TabList, Tab, TabPanels, TabPanel, Box, Text } from '@chakra-ui/react';
import withAuth from '@/HOCs/withAuth';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGetReturnCounts } from '@/services/returnOrders';

// Direct imports (no lazy loading)
import ReturnInitiated from './ReturnInitiated';
import ReturnInProgress from './ReturnInProgress';
import ReturnReceived from './ReturnReceived';
import ReturnRejected from './ReturnRejected';

interface TabCounts {
  initiated: number;
  inProgress: number;
  received: number;
  rejected: number;
}

const ReturnOrders = () => {
  const { state } = useLocation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [tabCounts, setTabCounts] = useState<TabCounts>({
    initiated: 0,
    inProgress: 0,
    received: 0,
    rejected: 0,
  });

  const { data: apiCounts, isLoading: countsLoading } = useGetReturnCounts();

  // Sync counts from API
  useEffect(() => {
    if (apiCounts) {
      setTabCounts({
        initiated: apiCounts.initiated_count || 0,
        inProgress: apiCounts.in_progress_count || 0,
        received: apiCounts.return_received_count || 0,
        rejected: apiCounts.rejected_count || 0,
      });
    }
  }, [apiCounts]);

  // Handle navigation via location.state.tabIndex
  useEffect(() => {
    if (state?.tabIndex !== undefined && state.tabIndex !== selectedTabIndex) {
      setSelectedTabIndex(state.tabIndex);
    }
  }, [state?.tabIndex]);

  const handleTabChange = useCallback((index: number) => {
    setSelectedTabIndex(index);
  }, []);

  const updateTabCount = useCallback((tabKey: keyof TabCounts, count: number) => {
    setTabCounts((prev) => (prev[tabKey] !== count ? { ...prev, [tabKey]: count } : prev));
  }, []);

  const tabs = useMemo(
    () => [
      {
        label: 'Return Initiated',
        key: 'initiated' as const,
        content: () => <ReturnInitiated updateCount={(c) => updateTabCount('initiated', c)} />,
      },
      {
        label: 'Return In-Progress',
        key: 'inProgress' as const,
        content: () => <ReturnInProgress updateCount={(c) => updateTabCount('inProgress', c)} />,
      },
      {
        label: 'Return Received',
        key: 'received' as const,
        content: () => <ReturnReceived updateCount={(c) => updateTabCount('received', c)} />,
      },
      {
        label: 'Rejected',
        key: 'rejected' as const,
        content: () => <ReturnRejected updateCount={(c) => updateTabCount('rejected', c)} />,
      },
    ],
    [updateTabCount],
  );

  return (
    <Flex pt={6} direction="column" width="100%" fontFamily="primary" px={6}>
      <Tabs index={selectedTabIndex} onChange={handleTabChange}>
        <TabList
          justifyContent={{ base: 'flex-start', md: 'space-around' }}
          width="100%"
          overflowX={{ base: 'auto', md: 'visible' }}
          whiteSpace="nowrap"
          borderBottom="1px solid"
          borderColor="border.default"
          py={{ base: 2, md: 0 }}
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              fontSize="sm"
              fontWeight="semibold"
              color="text.primary"
              display="flex"
              alignItems="center"
              gap={{ base: 1, md: 2 }}
              px={{ base: 3, md: 4 }}
              py={{ base: 2, md: 3 }}
              _selected={{
                borderBottom: '2px solid',
                borderColor: 'brand.primary.DEFAULT',
                color: 'brand.primary.DEFAULT',
              }}
              _hover={{ color: 'brand.primary.DEFAULT' }}
            >
              {tab.label}
              <Box
                minWidth={{ base: '20px', md: '24px' }}
                minHeight={{ base: '20px', md: '24px' }}
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                color={selectedTabIndex === index ? 'brand.primary.DEFAULT' : '#344054'}
                border="2px solid"
                borderColor={
                  selectedTabIndex === index ? 'brand.primary.DEFAULT' : 'border.default'
                }
                bg={selectedTabIndex === index ? 'brand.primary.50' : 'transparent'}
              >
                {countsLoading ? (
                  <Box
                    as="span"
                    w="12px"
                    h="12px"
                    borderRadius="full"
                    bg="gray.300"
                    animation="pulse"
                  />
                ) : (
                  <Text lineHeight={1.2}>{tabCounts[tab.key]}</Text>
                )}
              </Box>
            </Tab>
          ))}
        </TabList>

        <TabPanels bg="background.secondary" minH="600px">
          {tabs.map((tab, index) => (
            <TabPanel key={tab.key} px={0} py={4}>
              {/* Only mount the active tab's content */}
              {selectedTabIndex === index && tab.content()}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default withAuth(ReturnOrders);
